import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { getProductLineView, requestYieldCallback, shouldApplyProductLineView } from "@theme/utilities";

const LOG_PREFIX = "[VariantPicker]";

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Promise<void> | undefined} */
  #productDataPromise;

  /** @type {{ variants?: Array<Record<string, any>> } | undefined} */
  #productData;

  /** @type {Record<string, any> | undefined} */
  #pendingVariant;

  /** @type {boolean} */
  #didAutoSelectVorbestellung = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("change", this.variantChanged.bind(this));

    this.#prefetchProductData();

    requestYieldCallback(() => {
      if (!this.#isOnProductPageForAutoSelect()) return;
      const maybePromise = this.#ensureProductData();
      if (maybePromise && typeof maybePromise.finally === "function") {
        maybePromise.finally(() => {
          this.#autoSelectVorbestellungBatch();
        });
      } else {
        this.#autoSelectVorbestellungBatch();
      }
    });
  }

  /** Kick off background loading of product data to avoid first-change latency */
  #prefetchProductData() {
    if (!this.dataset.productId) return;

    requestYieldCallback(() => {
      if (this.#productData || this.#productDataPromise) return;

      this.#ensureProductData()?.catch((error) => {
        console.debug("[VariantPicker][Data] Prefetch failed", error);
      });
    });
  }

  /**
   * Returns Storefront API config for this instance.
   * @returns {{ token: string, apiVersion: string, endpoint: string }}
   */
  #getStorefrontConfig() {
    const token = (this.dataset.storefrontToken || "").trim();
    const apiVersion = (this.dataset.storefrontApiVersion || "2025-01").trim();
    const endpoint = `/api/${apiVersion}/graphql.json`;

    return { token, apiVersion, endpoint };
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  async variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    console.debug(LOG_PREFIX, "variantChanged", {
      tag: event.target.tagName,
      type:
        event.target instanceof HTMLInputElement
          ? event.target.type
          : undefined,
      value:
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLSelectElement
          ? event.target.value
          : undefined,
      optionOriginal: event.target.dataset.optionOriginal,
      optionValueId: event.target.dataset.optionValueId,
      variantId: event.target.dataset.variantId,
      inputId: event.target.dataset.inputId,
    });

    this.updateSelectedOption(event.target);

    try {
      await this.#ensureProductData();
    } catch (error) {
      console.error(LOG_PREFIX, "Unable to load product data", error);
    }

    console.debug(LOG_PREFIX, "Product data status", {
      hasProductData: Boolean(this.#productData),
      variantCount: Array.isArray(this.#productData?.variants)
        ? this.#productData.variants.length
        : 0,
    });

    const changedOptionPosition = this.getOptionPosition(event.target);
    let synchronizationResult = this.#synchronizeSelectionsWithVariant(
      changedOptionPosition,
    );

    if (!synchronizationResult?.variant) {
      const targetVariantId = event.target.dataset.variantId;
      if (targetVariantId && Array.isArray(this.#productData?.variants)) {
        const fallbackVariant = this.#productData.variants.find(
          (variant) => `${variant.id}` === `${targetVariantId}`,
        );
        if (fallbackVariant) {
          synchronizationResult = {
            variant: fallbackVariant,
            element: event.target,
          };
          console.debug(LOG_PREFIX, "Fallback variant by data-variant-id", {
            variantId: targetVariantId,
          });
        }
      }
    }

    console.debug(LOG_PREFIX, "Sync result", {
      changedOptionPosition,
      matched: Boolean(synchronizationResult?.variant),
      variantId: synchronizationResult?.variant?.id,
      option1: synchronizationResult?.variant?.option1,
      option2: synchronizationResult?.variant?.option2,
      option3: synchronizationResult?.variant?.option3,
    });

    this.#pendingVariant = synchronizationResult?.variant;

    const selectedVariant = synchronizationResult?.variant;
    const variantSourceElement =
      synchronizationResult?.element ||
      this.getSelectedVariantElement() ||
      event.target;

    if (variantSourceElement instanceof HTMLElement) {
      this.dispatchEvent(
        new VariantSelectedEvent({
          id: variantSourceElement.dataset.optionValueId ?? "",
        }),
      );
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    const shouldScrollToTop =
      isOnProductPage && this.getOptionPosition(event.target) === 1;

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl =
      (variantSourceElement instanceof HTMLElement &&
        variantSourceElement.dataset.connectedProductUrl) ||
      event.target.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(
      this.buildRequestUrl(
        variantSourceElement instanceof HTMLElement
          ? variantSourceElement
          : event.target,
      ),
      loadsNewProduct,
      this.#pendingVariant,
    );

    const url = new URL(window.location.href);

    let variantId;

    if (selectedVariant) {
      variantId = selectedVariant.id ? `${selectedVariant.id}` : null;
    } else if (
      variantSourceElement instanceof HTMLInputElement &&
      variantSourceElement.type === "radio"
    ) {
      variantId = variantSourceElement.dataset.variantId || null;
    } else if (variantSourceElement instanceof HTMLSelectElement) {
      const selectedOption =
        variantSourceElement.options[variantSourceElement.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    } else if (variantSourceElement instanceof HTMLOptionElement) {
      variantId = variantSourceElement.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = new URL(newUrl, window.location.origin).pathname;
    }

    const productLineView = getProductLineView();
    if (productLineView && shouldApplyProductLineView(this.dataset.productTags, this.dataset.productTemplateSuffix)) {
      url.searchParams.set("view", productLineView);
    } else {
      // Only remove view= if it was set by product line logic, to avoid stripping unrelated view params
      const currentView = url.searchParams.get("view");
      if (currentView === 'ortho' || currentView === 'protect' || currentView === 'default') {
        url.searchParams.delete("view");
      }
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }

    if (shouldScrollToTop) {
      requestYieldCallback(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`,
      );

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Ensures product data is loaded.
   * @returns {Promise<void> | undefined}
   */
  #ensureProductData() {
    if (
      this.#productData ||
      this.#productDataPromise ||
      !this.dataset.productId
    ) {
      return this.#productDataPromise;
    }

    const cacheKey = this.dataset.productId;
    const cached =
      window.__storefrontVariantsCache &&
      window.__storefrontVariantsCache[cacheKey];
    if (cached?.variants?.length) {
      console.debug(LOG_PREFIX, "Using cached Storefront variants", {
        count: cached.variants.length,
      });
      this.#productData = cached;
      return this.#productDataPromise;
    }

    const { token, apiVersion, endpoint } = this.#getStorefrontConfig();
    if (!token) {
      console.warn(LOG_PREFIX, "Missing Storefront token; skipping fetch.");
      return undefined;
    }

    const productId = this.dataset.productId;
    const gid =
      typeof productId === "string" && productId.startsWith("gid://")
        ? productId
        : `gid://shopify/Product/${productId}`;

    const queryWithInventory = `
      query GetAllVariants($id: ID!, $cursor: String) {
        product(id: $id) {
          variants(first: 250, after: $cursor) {
            edges {
              node {
                id
                availableForSale
                title
                selectedOptions { name value }
                quantityAvailable
                sku
                barcode
              }
              cursor
            }
            pageInfo { hasNextPage }
          }
        }
      }
    `;

    const queryWithoutInventory = `
      query GetAllVariants($id: ID!, $cursor: String) {
        product(id: $id) {
          variants(first: 250, after: $cursor) {
            edges {
              node {
                id
                availableForSale
                title
                selectedOptions { name value }
                sku
                barcode
              }
              cursor
            }
            pageInfo { hasNextPage }
          }
        }
      }
    `;

    const runQuery = async (query) => {
      const allVariants = [];
      let hasNextPage = true;
      let cursor = null;
      let page = 0;

      while (hasNextPage) {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": token,
          },
          body: JSON.stringify({ query, variables: { id: gid, cursor } }),
        });

        if (!response.ok) {
          throw new Error(
            `Storefront query failed: ${response.status} ${response.statusText}`,
          );
        }

        const json = await response.json();
        if (Array.isArray(json?.errors) && json.errors.length) {
          const err = new Error(
            json.errors[0]?.message || "Storefront query error",
          );
          err.errors = json.errors;
          throw err;
        }

        const connection = json?.data?.product?.variants;
        if (!connection) break;

        connection.edges.forEach((edge) => {
          if (edge?.node) allVariants.push(edge.node);
        });

        hasNextPage = Boolean(connection.pageInfo?.hasNextPage);
        cursor = hasNextPage
          ? (connection.edges[connection.edges.length - 1]?.cursor ?? null)
          : null;

        page += 1;
        console.debug(LOG_PREFIX, "Storefront page loaded", {
          page,
          pageSize: connection.edges.length,
          total: allVariants.length,
          hasNextPage,
        });
      }

      return allVariants;
    };

    console.debug(LOG_PREFIX, "Fetching Storefront variants", {
      gid,
      apiVersion,
    });

    this.#productDataPromise = runQuery(queryWithInventory)
      .catch((error) => {
        const message = (error?.message || "").toLowerCase();
        const errors = error?.errors;
        const inventoryAccessError =
          message.includes("quantityavailable") ||
          message.includes("unauthenticated_read_product_inventory") ||
          (Array.isArray(errors) &&
            errors.some((e) =>
              (e?.message || "").toLowerCase().includes("quantityavailable"),
            ));

        if (inventoryAccessError) {
          console.warn(
            LOG_PREFIX,
            "Inventory scope missing; retrying without quantityAvailable",
          );
          return runQuery(queryWithoutInventory);
        }

        throw error;
      })
      .then((variants) => {
        this.#productData = {
          variants: this.#normalizeApiVariants(variants),
        };

        if (cacheKey) {
          window.__storefrontVariantsCache =
            window.__storefrontVariantsCache || {};
          window.__storefrontVariantsCache[cacheKey] = this.#productData;
        }

        const preorderCount = Array.isArray(this.#productData.variants)
          ? this.#productData.variants.filter(
              (variant) => variant.inventory_policy === "continue",
            ).length
          : 0;

        console.debug(LOG_PREFIX, "Loaded Storefront variants", {
          count: this.#productData.variants.length,
          preorderCount,
        });

        const mergeStats = this.#mergeInventorySnapshot?.();
        if (mergeStats) {
          console.debug(LOG_PREFIX, "Snapshot merge result", mergeStats);
        }
      })
      .catch((error) => {
        console.error(LOG_PREFIX, "Storefront fetch failed", error);
        this.#productData = undefined;
        throw error;
      })
      .finally(() => {
        this.#productDataPromise = undefined;
      });

    return this.#productDataPromise;
  }

  /**
   * Normalizes variants returned from the Storefront API into the Shopify JS product format.
   * @param {Array<Record<string, any>>} variants
   * @returns {Array<Record<string, any>>}
   */
  #normalizeApiVariants(variants) {
    if (!Array.isArray(variants)) return [];

    const extractNumericId = (value) => {
      if (typeof value !== "string") return null;
      const numeric = value.split("/").pop();
      if (numeric && !Number.isNaN(Number(numeric))) return Number(numeric);
      return null;
    };

    const parseId = (id) => {
      const direct = extractNumericId(id);
      if (direct != null) return direct;
      if (typeof id !== "string") return id;
      try {
        const decoded = atob(id);
        const decodedId = extractNumericId(decoded);
        if (decodedId != null) return decodedId;
      } catch {
        // noop
      }
      return id;
    };

    return variants.map((variant) => {
      const selectedOptions = Array.isArray(variant?.selectedOptions)
        ? variant.selectedOptions.map((opt) =>
            typeof opt === "string" ? opt : (opt?.value ?? opt?.name),
          )
        : [];

      const option1 = (variant.option1 ?? selectedOptions[0] ?? "").trim();
      const option2 = (variant.option2 ?? selectedOptions[1] ?? "").trim();
      const option3 = (variant.option3 ?? selectedOptions[2] ?? "").trim();

      const policy = (variant.inventoryPolicy ?? variant.inventory_policy)
        ?.toString()
        .toLowerCase();
      const management = (
        variant.inventoryManagement ?? variant.inventory_management
      )
        ?.toString()
        .toLowerCase();

      const normalizeOptionValue = (value) =>
        typeof value === "string"
          ? value.toLowerCase().replace(/[\s\-_]/g, "")
          : "";

      const optionValues = [
        option1,
        option2,
        option3,
        ...selectedOptions,
      ].filter((value) => typeof value === "string" && value.length);

      const isPreorder = optionValues.some((value) => {
        const key = normalizeOptionValue(value);
        return key === "vorbestellung" || key === "preorder";
      });

      return {
        ...variant,
        id: parseId(variant.id),
        available:
          typeof variant.available === "boolean"
            ? variant.available
            : isPreorder
              ? true
              : Boolean(variant.availableForSale),
        option1: option1 || null,
        option2: option2 || null,
        option3: option3 || null,
        inventory_quantity:
          variant.inventory_quantity ??
          variant.inventoryQuantity ??
          variant.quantityAvailable,
        inventory_policy: isPreorder ? "continue" : policy,
        inventory_management: management || "shopify",
      };
    });
  }

  /** Reads server-rendered inventory snapshot (Liquid JSON) for this product */
  #getServerInventorySnapshot() {
    const pid = this.dataset.productId || this.#productData?.id;

    if (!pid) {
      console.debug(
        "[VariantPicker][Snapshot] Missing product id; cannot locate snapshot",
      );
      return null;
    }

    const el = document.getElementById(`variant-inventory-${pid}`);
    if (!el) {
      console.debug("[VariantPicker][Snapshot] No snapshot element for", pid);
      return null;
    }
    try {
      const parsed = JSON.parse(el.textContent || "[]");
      console.debug(
        "[VariantPicker][Snapshot] Parsed snapshot",
        pid,
        "variants=",
        Array.isArray(parsed) ? parsed.length : "∅",
      );
      return parsed;
    } catch {
      console.warn("[VariantPicker][Snapshot] Failed to parse snapshot JSON");
      return null;
    }
  }

  /** Merge snapshot truth into #productData.variants by id (adds missing variants) */
  #mergeInventorySnapshot() {
    if (!this.#productData) {
      console.debug("[VariantPicker][Snapshot] No product data to merge");
      return null;
    }

    if (!Array.isArray(this.#productData.variants)) {
      this.#productData.variants = [];
    }

    const snap = this.#getServerInventorySnapshot();
    if (!Array.isArray(snap) || !snap.length) {
      console.debug("[VariantPicker][Snapshot] No snapshot variants to merge");
      return null;
    }

    const normalizeQty = (value, fallback) =>
      Number.isFinite(+value) ? +value : fallback;

    const normalizeOptionValue = (value) =>
      typeof value === "string"
        ? value.toLowerCase().replace(/[\s\-_]/g, "")
        : "";

    const isPreorderVariant = (variant) => {
      if (!variant) return false;
      if ((variant.inventory_policy || "").toLowerCase() === "continue") {
        return true;
      }
      const values = [variant.option1, variant.option2, variant.option3].filter(
        (value) => typeof value === "string",
      );
      return values.some((value) => {
        const key = normalizeOptionValue(value);
        return key === "vorbestellung" || key === "preorder";
      });
    };

    const byId = new Map(
      this.#productData.variants.map((variant) => [
        String(variant.id),
        variant,
      ]),
    );

    let added = 0;
    let updated = 0;

    for (const snapVariant of snap) {
      const id = String(snapVariant.id);
      const target = byId.get(id);

      if (target) {
        updated += 1;
        target.inventory_quantity = normalizeQty(
          snapVariant.inventory_quantity,
          target.inventory_quantity,
        );
        target.inventory_policy =
          snapVariant.inventory_policy ?? target.inventory_policy;
        target.inventory_management =
          snapVariant.inventory_management ?? target.inventory_management;
        if (!isPreorderVariant(target)) {
          target.available =
            typeof snapVariant.available === "boolean"
              ? snapVariant.available
              : target.available;
        } else {
          target.available = true;
        }
        target.option1 = snapVariant.option1 ?? target.option1;
        target.option2 = snapVariant.option2 ?? target.option2;
        target.option3 = snapVariant.option3 ?? target.option3;
      } else {
        added += 1;
        const isPreorder = isPreorderVariant(snapVariant);
        const normalized = {
          id: snapVariant.id,
          available:
            typeof snapVariant.available === "boolean"
              ? snapVariant.available
              : isPreorder
                ? true
                : undefined,
          option1: snapVariant.option1,
          option2: snapVariant.option2,
          option3: snapVariant.option3,
          inventory_quantity: normalizeQty(
            snapVariant.inventory_quantity,
            undefined,
          ),
          inventory_policy: snapVariant.inventory_policy ?? undefined,
          inventory_management: snapVariant.inventory_management ?? undefined,
        };
        this.#productData.variants.push(normalized);
        byId.set(id, normalized);
      }
    }

    const stats = {
      snapshotVariants: snap.length,
      added,
      updated,
      productVariants: this.#productData.variants.length,
    };

    console.debug("[VariantPicker][Snapshot] Applied merge", stats);

    return stats;
  }

  /**
   * Synchronizes option selections with the closest matching available variant.
   * @param {number | null} changedOptionPosition - The position of the option that triggered the change event.
   * @returns {Record<string, any> | undefined}
   */
  #synchronizeSelectionsWithVariant(changedOptionPosition) {
    if (!this.#productData || !Array.isArray(this.#productData.variants)) {
      return undefined;
    }

    const optionValues = this.#collectSelectedOptionValues();

    if (typeof changedOptionPosition === "number") {
      for (
        let index = changedOptionPosition;
        index < optionValues.length;
        index += 1
      ) {
        optionValues[index] = undefined;
      }
    }

    // Always ignore option3 when choosing the best available batch.
    if (optionValues.length >= 3) {
      optionValues[2] = undefined;
    }

    let matchingVariant =
      this.#findMatchingVariant(optionValues, true) ||
      this.#findMatchingVariant(optionValues, false);

    if (!matchingVariant) {
      return undefined;
    }

    // On the product-template form, prefer a "continue"-policy variant that
    // shares the same option1 + option2. This keeps the ATC button active and
    // gives the split plan a valid overflow bucket when all deny-batch stock is
    // exhausted. Option 3 is always a dummy — the real batch is resolved on submit.
    if (
      this.dataset.templateProductMatch === "true" &&
      Array.isArray(this.#productData?.variants)
    ) {
      const continueVariant = this.#productData.variants.find(
        (v) =>
          (v.inventory_policy || "").toLowerCase() === "continue" &&
          v.option1 === matchingVariant.option1 &&
          v.option2 === matchingVariant.option2,
      );
      if (continueVariant) matchingVariant = continueVariant;
    }

    const variantElement = this.#applyVariantToOptions(matchingVariant);

    return { variant: matchingVariant, element: variantElement };
  }

  /**
   * Collects the currently selected option values.
   * @returns {(string[] | undefined)[]}
   */
  #collectSelectedOptionValues() {
    const values = [];

    const selectedRadios = Array.from(
      this.querySelectorAll('fieldset input[type="radio"]:checked'),
    );
    for (const input of selectedRadios) {
      const position = this.getOptionPosition(input);
      if (!position) continue;
      const candidates = [input.dataset.optionOriginal, input.value].filter(
        Boolean,
      );
      values[position - 1] = candidates;
    }

    const selects = Array.from(this.querySelectorAll("select"));
    for (const select of selects) {
      const option = select.options[select.selectedIndex];
      if (!option) continue;
      const position = this.getOptionPosition(option);
      if (!position) continue;
      const candidates = [option.dataset.optionOriginal, option.value].filter(
        Boolean,
      );
      values[position - 1] = candidates;
    }

    return values;
  }

  /**
   * On product page: force option3 to pre-order batch once on load.
   */
  #autoSelectVorbestellungBatch() {
    if (this.#didAutoSelectVorbestellung) return;
    if (!this.#productData || !Array.isArray(this.#productData.variants)) {
      return;
    }

    this.#didAutoSelectVorbestellung = true;

    const optionValues = this.#collectSelectedOptionValues();
    if (!optionValues.length || !optionValues[1]) return;

    const forcedOptionValues = [...optionValues];
    forcedOptionValues[2] = [
      "vorbestellung",
      "preorder",
      "pre-order",
      "pre order",
    ];

    const preferredVariant =
      this.#findMatchingVariant(forcedOptionValues, true) ||
      this.#findMatchingVariant(forcedOptionValues, false);

    if (!preferredVariant) {
      return;
    }

    const currentSelected = this.getSelectedVariantElement();
    const currentId = currentSelected?.dataset?.variantId;
    if (
      currentId &&
      preferredVariant?.id != null &&
      `${currentId}` === `${preferredVariant.id}`
    ) {
      return;
    }

    const variantElement = this.#applyVariantToOptions(preferredVariant);
    const triggerElement = variantElement || this.getSelectedVariantElement();

    if (triggerElement instanceof HTMLInputElement) {
      triggerElement.dispatchEvent(new Event("change", { bubbles: true }));
    } else if (triggerElement instanceof HTMLOptionElement) {
      triggerElement.parentElement?.dispatchEvent(
        new Event("change", { bubbles: true }),
      );
    }
  }

  #isOnProductPageForAutoSelect() {
    return (
      this.dataset.templateProductMatch === "true" &&
      !this.closest("product-card") &&
      !this.closest("quick-add-dialog") &&
      !this.closest("quick-add-component")
    );
  }

  /**
   * Finds the first variant that matches the provided option values.
   * @param {(string | undefined)[]} optionValues - The option values to match.
   * @param {boolean} onlyAvailable - If true, only consider available variants.
   * @returns {Record<string, any> | undefined}
   */
  #findMatchingVariant(optionValues, onlyAvailable) {
    const variants = Array.isArray(this.#productData?.variants)
      ? this.#productData.variants
      : [];

    const normalize = (value) => {
      if (typeof value !== "string") return "";
      let base = value.trim().toLowerCase();

      base =
        base === "schwarz"
          ? "black"
          : base === "braun"
            ? "brown"
            : base === "weiss" || base === "weiß"
              ? "white"
              : base === "blau"
                ? "blue"
                : base === "vorbestellung"
                  ? "preorder"
                  : base;

      base = base
        .replace(/½/g, ".5")
        .replace(/⅓/g, ".333")
        .replace(/⅔/g, ".667")
        .replace(/¼/g, ".25")
        .replace(/¾/g, ".75");

      return base.replace(/[\s\-_]/g, "");
    };

    const matches = variants.filter((variant) => {
      if (onlyAvailable && !variant.available) return false;

      return optionValues.every((value, index) => {
        if (!value) return true;
        const optionKey = `option${index + 1}`;
        const variantValue = normalize(variant[optionKey]);
        const candidates = Array.isArray(value) ? value : [value];
        return candidates.some(
          (candidate) => normalize(candidate) === variantValue,
        );
      });
    });

    return matches[0];
  }

  /**
   * Applies the provided variant's option values to the picker selections.
   * @param {Record<string, any>} variant - The variant to apply.
   */
  #applyVariantToOptions(variant) {
    /** @type {HTMLElement | undefined} */
    let variantElement;

    const variantId = variant?.id ? `${variant.id}` : undefined;

    for (let index = 0; index < 3; index += 1) {
      const optionValue = variant[`option${index + 1}`];
      if (!optionValue) continue;
      const selectedElement = this.#selectOptionValue(index + 1, optionValue);

      if (
        !variantElement &&
        variantId &&
        selectedElement instanceof HTMLElement
      ) {
        const elementVariantId =
          selectedElement instanceof HTMLInputElement ||
          selectedElement instanceof HTMLOptionElement
            ? selectedElement.dataset.variantId
            : undefined;

        if (elementVariantId === variantId) {
          variantElement = selectedElement;
        }
      }
    }

    return variantElement;
  }

  /**
   * Selects the option value for the specified position.
   * @param {number} position - The option position (1-based).
   * @param {string} value - The option value.
   * @returns {HTMLElement | undefined}
   */
  #selectOptionValue(position, value) {
    const radios = Array.from(
      this.querySelectorAll(
        `fieldset input[type="radio"][data-input-id^="${position}-"]`,
      ),
    );

    const matchingRadio = radios.find((input) => {
      const canonical = input.dataset.optionOriginal || input.value;
      return canonical === value;
    });

    if (matchingRadio) {
      this.updateSelectedOption(matchingRadio);
      return matchingRadio;
    }

    const selects = Array.from(this.querySelectorAll("select"));
    for (const select of selects) {
      const options = Array.from(select.options);
      const matchingOption = options.find((option) => {
        const positionId = this.getOptionPosition(option);
        if (positionId !== position) return false;
        const canonical = option.dataset.optionOriginal || option.value;
        return canonical === value;
      });

      if (
        matchingOption &&
        matchingOption.parentElement instanceof HTMLSelectElement
      ) {
        matchingOption.parentElement.value = matchingOption.value;
        this.updateSelectedOption(matchingOption.parentElement);
        return matchingOption;
      }
    }

    return undefined;
  }

  /**
   * Retrieves the selected element that contains a variant id.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined}
   */
  getSelectedVariantElement() {
    const selectedInput = this.querySelector(
      "fieldset input:checked[data-variant-id]",
    );

    if (selectedInput instanceof HTMLInputElement) {
      return selectedInput;
    }

    const selectedOption = this.querySelector(
      "select option[selected][data-variant-id]",
    );

    if (selectedOption instanceof HTMLOptionElement) {
      return selectedOption;
    }

    return undefined;
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
  ) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    const resolveVariantId = () => {
      if (this.#pendingVariant?.id != null) {
        return `${this.#pendingVariant.id}`;
      }

      if (selectedOption instanceof HTMLInputElement) {
        return selectedOption.dataset.variantId || null;
      }

      if (selectedOption instanceof HTMLOptionElement) {
        return selectedOption.dataset.variantId || null;
      }

      if (selectedOption instanceof HTMLSelectElement) {
        const option = selectedOption.options[selectedOption.selectedIndex];
        return option?.dataset?.variantId || null;
      }

      const fallback = this.getSelectedVariantElement();
      return fallback?.dataset?.variantId || null;
    };

    const variantId = resolveVariantId();
    if (variantId) {
      params.push(`variant=${variantId}`);
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (
      this.closest("quick-add-component") ||
      this.closest("swatches-variant-picker-component")
    ) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join(
        "&",
      )}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Gets the position of the option associated with the provided target element.
   * @param {Element} target - The element that triggered the variant change event.
   * @returns {number | null} The option position, or null if it cannot be determined.
   */
  getOptionPosition(target) {
    let inputId;

    if (target instanceof HTMLInputElement) {
      inputId = target.dataset.inputId;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      inputId = selectedOption?.dataset.inputId;
    } else if (target instanceof HTMLOptionElement) {
      inputId = target.dataset.inputId;
    }

    if (!inputId) return null;

    const [position] = inputId.split("-");
    const parsedPosition = Number.parseInt(position, 10);

    if (Number.isNaN(parsedPosition)) return null;

    return parsedPosition;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false, variantOverride) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    console.debug(LOG_PREFIX, "Fetch updated section", {
      requestUrl,
      shouldMorphMain,
    });

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`,
        )?.textContent;
        if (!textContent) return;

        let variantResource;

        try {
          const parsedVariant = JSON.parse(textContent);
          if (variantOverride) {
            variantResource = { ...parsedVariant, ...variantOverride };
          } else {
            variantResource = parsedVariant;
          }
        } catch (error) {
          console.error(
            LOG_PREFIX,
            "Unable to parse variant data from response",
            error,
          );
          variantResource = variantOverride;
        }

        this.#pendingVariant = undefined;

        if (!variantResource) {
          return;
        }

        const normalizeOptionValue = (value) =>
          typeof value === "string"
            ? value.toLowerCase().replace(/[\s\-_]/g, "")
            : "";

        const optionValues = [
          variantResource.option1,
          variantResource.option2,
          variantResource.option3,
        ].filter((value) => typeof value === "string" && value.length);

        const isPreorder = optionValues.some((value) => {
          const key = normalizeOptionValue(value);
          return key === "vorbestellung" || key === "preorder";
        });

        if (isPreorder) {
          variantResource.available = true;
          variantResource.availableForSale = true;
          variantResource.inventory_policy = "continue";
        }

        console.debug(LOG_PREFIX, "Variant resource resolved", {
          id: variantResource.id,
          available: variantResource.available,
          option1: variantResource.option1,
          option2: variantResource.option2,
          option3: variantResource.option3,
          isPreorder,
        });

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantResource, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct,
              }),
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.warn(LOG_PREFIX, "Fetch aborted by user");
        } else {
          console.error(LOG_PREFIX, "Fetch failed", error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase(),
    );

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      "select option[selected], fieldset input:checked",
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
